/**
 * @file        src/pages/platform/TenantSuppliersPage.jsx
 * @description Tenant Management → Supplier Credentials. The selected tenant's
 *              supplier API credentials (TripJack), encrypted at rest in the
 *              platform DB. Moved out of the tenant edit page's stacked panels
 *              into its own sidebar entry (?tenant=<id> picks the tenant).
 *              super_admin only — a tenant admin has no route to change these.
 *              The API never returns a stored key: the field is always blank
 *              and a blank submit means "keep the current key".
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-08-10 Claude — Initial (extracted from AddTenant.jsx unchanged, plus
 *                          the tenant picker wrapper).
 *   - 2026-09-17 Claude — The TripJack API key is now <PasswordInput>. It is
 *                          stored encrypted and shown only as a 4-char
 *                          preview, so entry is the one chance to check it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import { get, put } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import TenantPicker, { useTenantParam } from './TenantPicker';
import Field from './Field';
import PasswordInput from '../../components/PasswordInput';

const PROVIDER_LABEL = { tripjack: 'TripJack' };
const PROVIDER_HINT = {
  tripjack: 'Used for flight + hotel booking and the dashboard Cash Balance.',
};

export default function TenantSuppliersPage() {
  const [tenantId, setTenantId] = useTenantParam();
  return (
    <>
      <PageHead title="Supplier Credentials" subtitle="Platform · Tenant Management · supplier API keys" />
      <article className="panel glass" style={{ marginBottom: 16 }}>
        <TenantPicker value={tenantId} onChange={setTenantId} />
      </article>
      {tenantId ? <SupplierCredentials tenantId={tenantId} /> : (
        <article className="panel glass"><div className="empty">Select a tenant to manage its supplier credentials.</div></article>
      )}
    </>
  );
}

function SupplierCredentials({ tenantId }) {
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.tenants.supplierCredentials(tenantId),
    queryFn: () => get(`/tenants/${tenantId}/supplier-credentials`).then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: qk.tenants.supplierCredentials(tenantId) });
    qc.invalidateQueries({ queryKey: qk.tenants.readiness(tenantId) });
  };

  return (
    <article className="panel glass">
      <div className="panel-head">
        <div>
          <h2>Supplier API Credentials</h2>
          <p className="muted">
            Set by a super admin only — a tenant admin cannot change these.
            Keys are encrypted at rest and are never sent back to the browser.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="empty">Loading credentials…</div>
      ) : isError ? (
        <div className="empty"><p className="field-error">Could not load supplier credentials.</p></div>
      ) : (
        (data || []).map((c) => (
          <CredentialForm key={c.provider} tenantId={tenantId} credential={c} onSaved={refresh} />
        ))
      )}
    </article>
  );
}

function CredentialForm({ tenantId, credential, onSaved }) {
  const [baseUrl, setBaseUrl] = useState(credential.base_url || '');
  const [apiKey, setApiKey] = useState('');
  const [err, setErr] = useState({});

  const mut = useMutation({
    mutationFn: (body) => put(`/tenants/${tenantId}/supplier-credentials/${credential.provider}`, body),
    onSuccess: () => {
      setApiKey('');
      setErr({});
      toast.ok('Credentials saved', `${PROVIDER_LABEL[credential.provider] || credential.provider} updated.`);
      onSaved();
    },
    onError: (e) => {
      setErr(e.response?.data?.error?.fields || {});
      toast.err('Could not save', e.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    if (!baseUrl.trim()) return setErr({ base_url: 'Required' });
    if (!apiKey.trim() && !credential.configured) return setErr({ api_key: 'Required' });
    setErr({});
    const body = { base_url: baseUrl.trim(), status: 1 };
    if (apiKey.trim()) body.api_key = apiKey.trim();
    mut.mutate(body);
  }

  return (
    <form className="form-grid" onSubmit={onSubmit} style={{ marginTop: 8 }}>
      <Field label={`${PROVIDER_LABEL[credential.provider] || credential.provider} Base URL`} required
             err={err.base_url} hint={PROVIDER_HINT[credential.provider]}>
        <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
               placeholder="https://apitest.tripjack.com" />
      </Field>
      <Field label="API Key" required={!credential.configured} err={err.api_key}
             hint={credential.configured
               ? `Currently set (${credential.key_preview}). Leave blank to keep it.`
               : 'Not configured yet.'}>
        <PasswordInput value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                       placeholder="••••••••" autoComplete="new-password" />
      </Field>

      <div className="form-actions">
        <button className="btn primary" type="submit" disabled={mut.isPending}>
          <Icon name="check" /> {mut.isPending ? 'Saving…' : 'Save Credentials'}
        </button>
      </div>
    </form>
  );
}
